import { useRef, useState } from 'react';
import '../styles/RoadMap.css';
import { getEmailJsTemplateId, sendEmailJsForm } from '../utils/emailjsClient';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRecaptchaSiteKey } from '../utils/recaptcha';
import { useRecaptchaGate } from '../hooks/useRecaptchaGate';

const roadmap = {
  swe: {
    title: 'Så här går processen till',
    subtitle: 'En tydlig och strukturerad process från första mötet till rätt kandidat.',
    steps: [
      {
        number: '01',
        title: 'Boka möte',
        description: 'Vi inleder med ett första möte där vi går igenom er situation, era mål och vilket behov ni vill lösa.'
      },
      {
        number: '02',
        title: 'Behovsanalys',
        description: 'Om behovsbilden inte redan är helt definierad hjälper vi er att analysera rollen, kompetenskraven och vilken profil som passar bäst.'
      },
      {
        number: '03',
        title: 'Urval av kandidater',
        description: 'Vi identifierar och presenterar noggrant utvalda kandidater som matchar era krav och er verksamhet.'
      },
      {
        number: '04',
        title: 'Intervjuer och dialog',
        description: 'Ni träffar kandidaterna och får möjlighet att utvärdera både kompetens, personlighet och långsiktig matchning.'
      },
      {
        number: '05',
        title: 'Val och nästa steg',
        description: 'Ni väljer vilka kandidater ni vill gå vidare med, och vi stöttar er genom den sista delen av processen fram till start.'
      }
    ]
  },
  eng: {
    title: 'How the Process Works',
    subtitle: 'A clear and structured process from the first meeting to the right candidate.',
    steps: [
      {
        number: '01',
        title: 'Book a Meeting',
        description: 'We start with an initial meeting to understand your situation, goals, and the type of competence you need.'
      },
      {
        number: '02',
        title: 'Needs Analysis',
        description: 'If the role is not yet fully defined, we help you analyze the position, required skills, and the right candidate profile.'
      },
      {
        number: '03',
        title: 'Selected Candidates',
        description: 'We identify and present carefully selected candidates who match your needs and business context.'
      },
      {
        number: '04',
        title: 'Interviews and Dialogue',
        description: 'You meet the candidates and evaluate both competence, personality, and long-term fit.'
      },
      {
        number: '05',
        title: 'Selection and Next Steps',
        description: 'You decide which candidates to move forward with, and we support you through the final stage up to placement.'
      }
    ]
  }
};

const cta = {
  swe: {
    eyebrow: 'Få hjälp idag',
    heading: 'Osäker på hur du börjar?',
    text: 'Skicka dina uppgifter så kontaktar vi dig för ett första möte.',
    name: 'Namn',
    company: 'Företagsnamn',
    phone: 'Telefonnummer',
    email: 'E-post',
    button: 'Skicka',
    recaptchaHint: 'Fyll i alla obligatoriska fält ovan för att kunna använda reCAPTCHA.'
  },
  eng: {
    eyebrow: 'Get help today',
    heading: 'Not sure where to start?',
    text: 'Send your details and we will contact you for the first meeting.',
    name: 'Name',
    company: 'Company name',
    phone: 'Phone number',
    email: 'Email',
    button: 'Send',
    recaptchaHint: 'Complete all required fields above to use reCAPTCHA.'
  }
};

function RoadMap({ lang = 'swe' }) {
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const text = roadmap[lang] || roadmap.swe;
  const ctaText = cta[lang] || cta.swe;
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const [recaptchaAllowed, syncRecaptchaGate] = useRecaptchaGate(formRef, captchaRef, setCaptchaToken);
  const captchaExpiredMessage = lang === 'swe'
    ? 'Verifieringen gick ut. Bekräfta att du inte är en robot igen.'
    : 'Verification expired. Please confirm you are not a robot again.';
  const captchaErrorMessage = lang === 'swe'
    ? 'reCAPTCHA kunde inte laddas klart. Ladda om sidan och försök igen.'
    : 'reCAPTCHA could not finish loading. Refresh the page and try again.';

  const handlePhoneChange = (event) => {
    const raw = event.target.value;
    const filtered = raw.replace(/[^0-9+\s-]/g, '');
    setPhone(filtered);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaExpiredMessage);
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaErrorMessage);
    captchaRef.current?.reset?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const formEl = formRef.current;
    if (!formEl) {
      return;
    }
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    setStatus('sending');

    try {
      const siteKey = getRecaptchaSiteKey();
      const token = captchaRef.current?.getValue?.() || captchaToken;
      if (siteKey && !token) {
        setStatus('idle');
        setError(lang === 'swe' ? 'Bekräfta att du inte är en robot.' : 'Please confirm you are not a robot.');
        return;
      }

      const timeEl = formEl.querySelector('input[name="time"]');
      if (timeEl) {
        timeEl.value = new Date().toLocaleString(lang === 'swe' ? 'sv-SE' : 'en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      await sendEmailJsForm(formEl, getEmailJsTemplateId('cta'));
      setStatus('sent');
      formEl.reset();
      setPhone('');
      syncRecaptchaGate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('EmailJS send failed (cta)', err);
      setStatus('idle');
      setError(lang === 'swe' ? 'Kunde inte skicka just nu. Försök igen.' : 'Could not send right now. Please try again.');
    }
  };

  return (
    <section className="process-section" id="process">
      <div className="process-header">
        <p className="process-eyebrow">Process</p>
        <h2 className="process-title">{text.title}</h2>
        <p className="process-subtitle">{text.subtitle}</p>
      </div>

      <div className="process-roadmap">
        {text.steps.map((step, index) => (
          <article className="process-step" key={`roadmap-step-${step.number}`}>
            <div className="process-top">
              <div className="process-number">{step.number}</div>
            </div>
            <h3 className="process-step-title">{step.title}</h3>
            <p className="process-step-description">{step.description}</p>
          </article>
        ))}
      </div>

      <div className="cta-block" id="contact">
        <div className="cta-content">
          <p className="cta-eyebrow">{ctaText.eyebrow}</p>
          <h3 className="cta-heading">{ctaText.heading}</h3>
          <p className="cta-text">{ctaText.text}</p>
        </div>
        <form
          ref={formRef}
          className="cta-form"
          onSubmit={handleSubmit}
          onInput={syncRecaptchaGate}
          onChange={syncRecaptchaGate}
        >
          <input type="hidden" name="source" value="cta" />
          <input type="hidden" name="time" value="" />
          <input type="hidden" name="context_title" value={lang === 'swe' ? 'CTA: Osäker på hur du börjar?' : 'CTA: Not sure where to start?'} />
          <div className="cta-row">
            <label>
              <span>{ctaText.name}</span>
              <input type="text" name="name" placeholder="Anna Andersson" required />
            </label>
            <label>
              <span>{ctaText.company}</span>
              <input type="text" name="company" placeholder="Acme AB" required />
            </label>
          </div>
          <div className="cta-row">
            <label>
              <span>{ctaText.phone}</span>
              <input
                type="tel"
                name="phone"
                placeholder="070-123 45 67"
                required
                title={lang === 'swe' ? 'Ange bara siffror, mellanslag eller bindestreck' : 'Use only numbers, spaces, or dashes'}
                value={phone}
                onChange={handlePhoneChange}
              />
            </label>
            <label>
              <span>{ctaText.email}</span>
              <input type="email" name="email" placeholder="anna@acme.se" required />
            </label>
          </div>

          {getRecaptchaSiteKey() && (
            <>
              <div
                className={`recaptcha-wrap${recaptchaAllowed ? '' : ' recaptcha-wrap--gated'}`}
                aria-disabled={!recaptchaAllowed}
              >
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={getRecaptchaSiteKey()}
                  onChange={(token) => setCaptchaToken(token || '')}
                  onExpired={handleCaptchaExpired}
                  onErrored={handleCaptchaErrored}
                  hl={lang === 'swe' ? 'sv' : 'en'}
                />
              </div>
              {!recaptchaAllowed && (
                <p className="recaptcha-hint" role="status">
                  {ctaText.recaptchaHint}
                </p>
              )}
            </>
          )}

          <button type="submit" className="cta-submit-btn cta-btn" disabled={status === 'sending'}>
            {status === 'sending' ? (lang === 'swe' ? 'Skickar…' : 'Sending…') : ctaText.button}
          </button>
          {error && <p className="cta-status">{error}</p>}
          {status === 'sent' && !error && (
            <p className="cta-status">{lang === 'swe' ? 'Tack! Vi hör av oss snart.' : 'Thank you! We will get back to you shortly.'}</p>
          )}
        </form>
      </div>
    </section>
  );
}

export default RoadMap;
